import {getCurrentProductCode} from '@common/api/productContext';
import {buildFailedOnboardingInstallRoute, buildOnboardingInstallProcessingRoute, isFullPageShopifyReturn, hasExpectedShopifyInstallation, readOnboardingInstall} from '@/flows/shopifyOnboardingInstall';
import { shouldHideShopifyCallback } from "@/contexts/AuthContext/shopifyOnboardingResume";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { authService } from "@common/api/authService";
import { useAuth } from "@/contexts/AuthContext";
import { buildOnboardingRoutePreservingInvite } from "@/components/Onboarding/inviteRedirect";
import {
  buildShopifyInstallBillingRecoveryUrl,
  buildShopifyInstallCallbackUrl,
  buildShopifyInstallSuccessRoute,
  clearShopifyInstallContext,
  isOnboardingShopifyInstall,
  readShopifyInstallContext,
  readShopifyInstallWorkspaceTarget,
} from "@/flows/shopifyInstall";
import {
  ShopifyInstallController,
  type InstallInput,
  type InstallState,
} from "@/flows/shopifyInstallController";
import { logSentryEvent } from "@/lib/sentry";
import ShopifyInstallView from "./ShopifyInstallView";
import PopupShopifyInstall from "./PopupShopifyInstall";
import { readPopupInstallForWindow } from "@/flows/shopifyPopupInstall";

function LegacyShopifyInstallPage() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const ticket = readShopifyInstallContext(location.search);
  const isCallback = shouldHideShopifyCallback({
    currentSearch: location.search,
  });
  const requestedWorkspace = useRef({
    ticket,
    workspaceId: readShopifyInstallWorkspaceTarget(location.search),
  });
  if (requestedWorkspace.current.ticket !== ticket)
    requestedWorkspace.current = {
      ticket,
      workspaceId: readShopifyInstallWorkspaceTarget(location.search),
    };
  const [selection, setSelection] = useState<{
    accountId: string;
    ticket: string;
    workspaceId: string;
  } | null>(null);
  const accountId = auth.user?.id || "";
  const selectedWorkspaceId =
    selection?.accountId === accountId && selection.ticket === ticket
      ? selection.workspaceId
      : requestedWorkspace.current.workspaceId ||
        auth.currentWorkspace?.id ||
        "";
  const completed = useRef<{
    accountId: string;
    workspaceId: string;
    destination: string;
  } | null>(null);
  const [state, setState] = useState<InstallState>({ kind: "checking" });
  const input: InstallInput = {
    ticket,
    accountId,
    bootstrapReady: Boolean(
      auth.user &&
      auth.profile &&
      !auth.isLoadingProfile &&
      !auth.isLoadingWorkspaces &&
      !auth.workspacesError,
    ),
    workspaceIds: auth.workspaces.map((workspace) => workspace.id),
    selectedWorkspaceId,
    currentWorkspaceId: auth.currentWorkspace?.id || "",
    syncedWorkspaceId: window.Bustly?.workspace_id || "",
  };
  const inputKey = JSON.stringify(input);
  const latest = useRef({ auth, location, inputKey });
  latest.current = { auth, location, inputKey };
  const authorization = useRef<AbortController | null>(null);
  const controller = useMemo(
    () =>
      new ShopifyInstallController({
        resolve: (context) => authService.resolveShopifyInstallContext(context),
        connect: (shop, context, workspaceId) => {
          authorization.current?.abort();
          authorization.current = new AbortController();
          return latest.current.auth.integrations.connect("shopify", {
            shop_domain: shop,
            install_context: context,
            redirect_url: buildShopifyInstallCallbackUrl(
              window.location.origin,
              workspaceId,
            ),
            connection_scene: isOnboardingShopifyInstall(
              latest.current.location.search,
              workspaceId,
            )
              ? "onboarding"
              : "admin",
          }, {signal: authorization.current.signal});
        },
        verifyReuse: async (workspaceId, context) => {
          const inventory = await authService.getWorkspaceIntegrations({signal: authorization.current?.signal, headers: {
            "X-Workspace-Id": workspaceId,
            "X-Bustly-Product": getCurrentProductCode(),
          }});
          return hasExpectedShopifyInstallation(inventory, context.shop_domain, context.oauth_app_id);
        },
        changed: setState,
        isCurrent: (requestInput) =>
          JSON.stringify(requestInput) === latest.current.inputKey &&
          requestInput.ticket ===
            readShopifyInstallContext(window.location.search) &&
          requestInput.syncedWorkspaceId ===
            (window.Bustly?.workspace_id || ""),
        succeeded: (workspaceId) => {
          const current = latest.current.location;
          completed.current = {
            accountId: latest.current.auth.user?.id || "",
            workspaceId,
            destination: buildShopifyInstallSuccessRoute(
              current.search,
              workspaceId,
            ),
          };
          navigate(
            {
              pathname: current.pathname,
              search: clearShopifyInstallContext(current.search),
              hash: current.hash,
            },
            { replace: true },
          );
        },
      }),
    [navigate],
  );
  useLayoutEffect(() => {
    if (isCallback) return;
    if (
      !ticket &&
      completed.current?.accountId === accountId &&
      completed.current.workspaceId === input.currentWorkspaceId
    )
      return;
    authorization.current?.abort();
    completed.current = null;
    controller.update(input);
  }, [controller, inputKey, isCallback]);
  useLayoutEffect(() => () => { authorization.current?.abort(); controller.dispose(); }, [controller]);
  useLayoutEffect(() => {
    logSentryEvent("shopify.install.state", {
      area: "shopify_install",
      route_kind: "shopify-install",
      state: state.kind,
      has_install_context: Boolean(ticket),
    });
  }, [state.kind, Boolean(ticket)]);

  const abandon = () => {
    authorization.current?.abort();
    controller.dispose();
    navigate({ pathname: "/profile", search: "" }, { replace: true });
  };
  // Existing AdminProvider broadcasts the callback result and closes the popup.
  if (isCallback) return null;
  return (
    <ShopifyInstallView
      state={state}
      workspaces={auth.workspaces}
      selectedWorkspaceId={selectedWorkspaceId}
      workspaceError={Boolean(auth.workspacesError)}
      onSelect={(workspaceId) => {
        if (!auth.workspaces.some((workspace) => workspace.id === workspaceId))
          return;
        setSelection({ accountId, ticket, workspaceId });
        auth.switchWorkspace(workspaceId, { preservePath: true });
      }}
      onConnect={() => void controller.connect()}
      onCreateWorkspace={() =>
        navigate(
          buildOnboardingRoutePreservingInvite(
            location.search,
            location.pathname,
          ),
        )
      }
      onContinue={() =>
        navigate(completed.current?.destination || "/profile", {
          replace: true,
        })
      }
      onAbandon={abandon}
      onRetryWorkspace={() =>
        void auth.refreshWorkspaces({
          skipEmptyRedirect: true,
          preferredWorkspaceId: selectedWorkspaceId || null,
        })
      }
      onRetryValidation={() => controller.retryValidation()}
      onReviewPlan={() =>
        window.open(
          buildShopifyInstallBillingRecoveryUrl(
            window.location.origin,
            selectedWorkspaceId,
          ),
          "_blank",
          "noopener,noreferrer",
        )
      }
      onChangeAccount={() => auth.logout()}
    />
  );
}

export default function ShopifyInstallPage() {
  const auth = useAuth();
  const location = useLocation();
  // Only the returning window's attempt may own this ticket. A new tab with no
  // reference uses the existing explicit workspace-selection journey.
  let popupIntent = null;
  try {
    popupIntent = readShopifyInstallContext(location.search)
      ? readPopupInstallForWindow(window.localStorage, window.sessionStorage, {
          accountId: auth.user?.id || '',
          productCode: getCurrentProductCode(),
        })
      : null;
  } catch {}
  if (popupIntent) return <PopupShopifyInstall key={popupIntent.attemptId} intent={popupIntent} />;
  let intent = null;
  try { intent = readOnboardingInstall(window.sessionStorage, { accountId: auth.user?.id || '', productCode: getCurrentProductCode() }); } catch {}
  // Once the authenticated account matches its intent, onboarding owns bootstrap
  // and connection progress; unrelated global work must not delay this handoff.
  if (intent && (isFullPageShopifyReturn(location.search) || readShopifyInstallContext(location.search))) return <Navigate to={buildOnboardingInstallProcessingRoute(intent, location.search)} replace />;
  if (auth.isLoadingProfile || auth.isGlobalLoading) return null;
  if (isFullPageShopifyReturn(location.search)) return <Navigate to={buildFailedOnboardingInstallRoute(location.search)} replace />;
  return <LegacyShopifyInstallPage />;
}
